import { useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { KeycloakLoginTransition } from "@/components/auth/KeycloakLoginTransition";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";

type Permissions = ReturnType<typeof usePermissions>;

/**
 * The permission gate for migrated routes.
 *
 * The legacy tree expresses this by nesting <ProtectedRoute><HideInferenceOnlyRoute>
 * around each element, and those components cannot be reused here: they redirect
 * with react-router's <Navigate>, which throws outside the legacy <Router>.
 *
 * The RULES are what matter and they are reproduced exactly:
 *   - auth still resolving      -> render nothing but a loading state
 *   - not signed in             -> start the shared Keycloak login transition
 *   - permission check fails    -> send to "/", same as <Navigate to="/" replace />
 *
 * Redirecting from an effect rather than during render is deliberate — navigating
 * while rendering is a React error, and the legacy version got away with it only
 * because <Navigate> defers internally.
 *
 * Every migrated route must wrap its content in this. Dropping it is a silent
 * privilege escalation: the route simply renders for users the legacy tree would
 * have turned away.
 */
export const RouteGuard = ({
  allow,
  children,
}: {
  /** Mirrors the legacy guard, e.g. (p) => !p.isInferenceOnly */
  allow?: (permissions: Permissions) => boolean;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { currentUser, loading } = useAuth();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const authResolved = !loading && currentUser !== undefined;
  const permitted = allow ? allow(permissions) : true;

  useEffect(() => {
    if (authResolved && currentUser && !permitted) {
      navigate({ to: "/", replace: true });
    }
  }, [authResolved, currentUser, permitted, navigate]);

  if (!authResolved) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-screen w-full items-center justify-center text-sm text-muted-foreground"
      >
        {t("common.loading")}
      </div>
    );
  }

  if (!currentUser) {
    return <KeycloakLoginTransition returnPath={pathname} />;
  }

  if (!permitted) return null;

  return <>{children}</>;
};
